import React, { useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import isEmpty from "lodash.isempty";

import {
  ActionGeneric,
  HvBulkActions,
  HvEmptyState,
  HvPagination,
} from "@hitachivantara/uikit-react-core";
import {
  HvTable,
  HvTableBody,
  HvTableCell,
  HvTableContainer,
  HvTableHead,
  HvTableHeader,
  HvTableRow,
  HvTableOptions,
  HvRowInstance,
  HvTableInstance,
} from "@hitachivantara/uikit-react-lab";
import { Ban } from "@hitachivantara/uikit-react-icons";
import { nodeFallback } from "lib/utils/fallbacks";
import useStyles from "./styles";
import OverflowTooltip from "../OverflowTooltip";
import LoadingContainer from "../../layout/LoadingContainer";

interface SimpleTableProps<T extends Record<string, unknown>>
  extends HvTableOptions<T> {
  instance: HvTableInstance<T>;
  totalCount?: number;
  bulkActions?: ActionGeneric[];
  getExpandedRowContent?: (
    row: HvRowInstance<T>
  ) => React.ReactNode | undefined;
  disableSelection?: boolean;
  disablePagination?: boolean;
  hideSelectAllPages?: boolean;
  selectAllFiltered?: boolean;
  loading?: boolean;
  labels?: Record<string, string>;
  defaultPageSize?: number;
}

export const DEFAULT_PAGE = 0;
export const DEFAULT_PAGE_SIZE = 10;

const SimpleTable = <T extends Record<string, unknown>>({
  instance,
  totalCount = 0,
  bulkActions,
  getExpandedRowContent,
  disableSelection = false,
  disablePagination = false,
  hideSelectAllPages = false,
  selectAllFiltered = false,
  loading = false,
  labels,
  defaultPageSize = DEFAULT_PAGE_SIZE,
}: SimpleTableProps<T>): JSX.Element => {
  const classes = useStyles();

  const { t } = useTranslation("common");

  const paginationLabels = useMemo(
    () => ({
      pageSizePrev: t("simple_table.pageSizePrefixLabel"),
      pageSizeEntryName: t("simple_table.pageSizeSuffixLabel"),
      pageSizeSelectorDescription: t("simple_table.pageSizeSelectorAriaLabel"),
      pagesSeparator: t("simple_table.pagesSeparator"),
      paginationFirstPageTitle: t("simple_table.firstPageButtonTooltip"),
      paginationPreviousPageTitle: t("simple_table.previousPageButtonTooltip"),
      paginationNextPageTitle: t("simple_table.nextPageButtonTooltip"),
      paginationLastPageTitle: t("simple_table.lastPageButtonTooltip"),
      paginationInputLabel: t("simple_table.currentPageInputAriaLabel"),
      firstPage: t("simple_table.firstPageButtonAriaLabel"),
      previousPage: t("simple_table.previousPageButtonAriaLabel"),
      nextPage: t("simple_table.nextPageButtonAriaLabel"),
      lastPage: t("simple_table.lastPageButtonAriaLabel"),
    }),
    [t]
  );

  const {
    getTableProps,
    getTableBodyProps,
    prepareRow,
    headers,
    rows,
    page,
    state: { pageSize, selectedRowIds, lockedSelectionRowIds = {} },
    getHvBulkActionsProps,
    getHvPaginationProps,
    manualPagination,
  } = instance;

  const selectedRowIdsKeys = Object.keys(selectedRowIds || {});

  const visibleRows = page ?? rows;

  const disabledBulkActionsCheckbox = !isEmpty(lockedSelectionRowIds)
    ? Object.values(lockedSelectionRowIds).reduce(
        (prev, value) => prev && value,
        true
      )
    : false;

  const EmptyRow = useCallback(
    () => (
      <HvTableRow>
        <HvTableCell
          className={classes.noData}
          // colSpan is missing in the TS definition
          {...{ colSpan: headers.length }}
        >
          <HvEmptyState
            message={labels?.noData ?? t("simple_table.noDataMessage")}
            icon={<Ban role="presentation" />}
          />
        </HvTableCell>
      </HvTableRow>
    ),
    [classes.noData, headers.length, labels?.noData, t]
  );
  const bulkActionProps = getHvBulkActionsProps?.();
  return (
    <LoadingContainer hidden={!loading}>
      {!disableSelection && (
        <HvBulkActions
          selectAllLabel={t("simple_table.all")}
          selectAllPagesLabel={
            labels?.selectAllPagesLabel !== ""
              ? labels?.selectAllPagesLabel
              : undefined
          }
          {...bulkActionProps}
          onSelectAll={() => {
            if (selectAllFiltered) {
              bulkActionProps.onSelectAllPages();
              return;
            }
            bulkActionProps.onSelectAll();
          }}
          numTotal={totalCount}
          numSelected={selectedRowIdsKeys.length}
          actions={bulkActions}
          showSelectAllPages={!hideSelectAllPages && manualPagination !== true}
          checkboxProps={{
            disabled: !!disabledBulkActionsCheckbox,
          }}
        />
      )}
      <HvTableContainer style={{ overflow: "visible" }}>
        <HvTable {...getTableProps()} style={{ tableLayout: "fixed" }}>
          <HvTableHead>
            <HvTableRow>
              {headers.map((col) => (
                <HvTableHeader {...col.getHeaderProps()}>
                  <OverflowTooltip
                    data={nodeFallback(col.render("Header"))}
                    paragraphOverflow
                  />
                </HvTableHeader>
              ))}
            </HvTableRow>
          </HvTableHead>
          <HvTableBody {...getTableBodyProps()}>
            {visibleRows.length > 0 ? (
              visibleRows.map((row) => {
                prepareRow(row);

                const { key, ...rowProps } = row.getRowProps();

                return (
                  <React.Fragment key={key}>
                    <HvTableRow {...rowProps} style={{ height: 40 }}>
                      {row.cells.map((cell) => (
                        <HvTableCell {...cell.getCellProps()}>
                          {cell.render("Cell")}
                        </HvTableCell>
                      ))}
                    </HvTableRow>
                    {getExpandedRowContent != null && row.isExpanded
                      ? getExpandedRowContent(row)
                      : null}
                  </React.Fragment>
                );
              })
            ) : (
              <EmptyRow />
            )}
          </HvTableBody>
        </HvTable>
      </HvTableContainer>
      {!disablePagination &&
        (pageSize !== defaultPageSize || totalCount > pageSize) &&
        rows.length > 0 && (
          <HvPagination {...getHvPaginationProps()} labels={paginationLabels} />
        )}
    </LoadingContainer>
  );
};

export default SimpleTable;
